import type {
  AddTracksRequest,
  AddTracksResponse,
  ApiErrorResponse,
  CreatePlaylistRequest,
  Playlist,
  PlaylistListResponse,
  PlaylistMergePatch,
  PlaylistResponse,
  YearOverride,
} from '@open-songster/shared'
import { MERGE_PATCH_MEDIA_TYPE } from '@open-songster/shared'
import { config } from '../lib/config'
import { isRecord } from '../lib/storage'
import { getApiToken } from './auth'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Revalidation is left to the browser's HTTP cache: every GET answers with an
 * ETag and `Cache-Control: private, no-cache`, so the browser sends the
 * conditional request itself and a 304 never reaches this code.
 */
async function request<T>(
  path: string,
  init: { method: string; body?: unknown; contentType?: string } = { method: 'GET' },
): Promise<T> {
  const token = await getApiToken()
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: init.method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(init.body === undefined
        ? {}
        : { 'content-type': init.contentType ?? 'application/json' }),
    },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  })

  if (response.status === 204) return undefined as T
  const text = await response.text()
  const payload: unknown = text.length > 0 ? safeParse(text) : null

  if (!response.ok) {
    throw new ApiError(errorMessage(payload, response.status), response.status, errorCode(payload))
  }
  return payload as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function errorBody(payload: unknown): ApiErrorResponse['error'] | null {
  if (!isRecord(payload) || !isRecord(payload.error)) return null
  const { code, message } = payload.error
  if (typeof code !== 'string' || typeof message !== 'string') return null
  return { code: code as ApiErrorResponse['error']['code'], message }
}

function errorMessage(payload: unknown, status: number): string {
  return errorBody(payload)?.message ?? `Request failed with status ${status}`
}

function errorCode(payload: unknown): string {
  return errorBody(payload)?.code ?? 'internal_error'
}

function playlistPath(id: string, ...rest: string[]): string {
  return [`/playlists/${encodeURIComponent(id)}`, ...rest.map(encodeURIComponent)].join('/')
}

function patch(id: string, body: PlaylistMergePatch): Promise<Playlist> {
  return request<PlaylistResponse>(playlistPath(id), {
    method: 'PATCH',
    body,
    contentType: MERGE_PATCH_MEDIA_TYPE,
  }).then((r) => r.playlist)
}

export const apiClient = {
  listPlaylists: () => request<PlaylistListResponse>('/playlists'),

  getPlaylist: (id: string) => request<PlaylistResponse>(playlistPath(id)).then((r) => r.playlist),

  /** Creates a playlist — empty, or seeded with tracks read from Spotify. */
  createPlaylist: (body: CreatePlaylistRequest): Promise<Playlist> =>
    request<PlaylistResponse>('/playlists', { method: 'POST', body }).then((r) => r.playlist),

  /**
   * Merges tracks into a playlist. Never removes anything: the stored playlist
   * is the source of truth and Spotify only ever a place to import from.
   */
  addTracks: (id: string, body: AddTracksRequest): Promise<AddTracksResponse> =>
    request<AddTracksResponse>(playlistPath(id, 'tracks'), { method: 'POST', body }),

  removeTrack: (id: string, trackId: string): Promise<Playlist> =>
    request<PlaylistResponse>(playlistPath(id, 'tracks', trackId), { method: 'DELETE' }).then(
      (r) => r.playlist,
    ),

  /** Re-runs year resolution for one track, synchronously. */
  resolveTrack: (id: string, trackId: string): Promise<Playlist> =>
    request<PlaylistResponse>(playlistPath(id, 'tracks', trackId, 'resolve'), {
      method: 'POST',
    }).then((r) => r.playlist),

  /**
   * Merge-patch semantics mean only the named members are touched, so two
   * curators editing different tracks cannot clobber each other's work.
   */
  patchPlaylist: patch,

  rename: (id: string, name: string): Promise<Playlist> => patch(id, { name }),

  setTracksPerYear: (id: string, tracksPerYear: number): Promise<Playlist> =>
    patch(id, { tracksPerYear }),

  setAllowUneven: (id: string, allowUnevenDistribution: boolean): Promise<Playlist> =>
    patch(id, { allowUnevenDistribution }),

  /** Sets or clears one track's year; `null` restores what resolution found. */
  /** A typed year, a provider to follow, or null to go back to resolution. */
  setTrackYear: (id: string, trackId: string, year: YearOverride | null): Promise<Playlist> =>
    patch(id, { yearOverrides: { [trackId]: year } }),

  /** Sets or clears one track's curated title; `null` restores Spotify's. */
  setTrackTitle: (id: string, trackId: string, title: string | null): Promise<Playlist> =>
    patch(id, { titleOverrides: { [trackId]: title } }),

  /** Many titles in one patch — what a find-and-replace produces. */
  setTrackTitles: (id: string, titles: Record<string, string | null>): Promise<Playlist> =>
    patch(id, { titleOverrides: titles }),

  deletePlaylist: (id: string) => request<void>(playlistPath(id), { method: 'DELETE' }),
}
